import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, TextField, Typography, Select, MenuItem, FormControl, InputLabel, Collapse, Alert, List, ListItem, IconButton, ListItemText } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import api from '../../../../api/api';
import { Trash } from 'lucide-react';
import Loading from '../../../../components/loading';
import { useParams } from 'react-router-dom';

export default function PrescriptionModalWeb({ open, onClose, onSave }) {

    const { patientId } = useParams();

    const [isLoading, setLoading] = useState(false);

    const [comboBoxDentists, setComboBoxDentists] = useState([]);
    const [selectedDentistId, setSelectedDentistId] = useState('');

    const [medicinesToSave, setMedicinesToSave] = useState([]);
    const [date, setDate] = useState(dayjs());
    const [medicineToAdd, setMedicineToAdd] = useState('');
    const [quantityToAdd, setQuantityToAdd] = useState('');
    const [dosageToAdd, setDosageToAdd] = useState('');
    const [modalError, setModalError] = useState('');


    const getComboBoxDentists = useCallback(() => {
        api.get("/dentists/combobox", {
            headers: {
                Authorization: sessionStorage.getItem('token')
            }
        }).then((returnFromApi) => {
            setComboBoxDentists(returnFromApi.data);
        }).catch((err) => {
            setLoading(false);
            console.error("ops! ocorreu um erro" + err);
        });
    }, []);


    useEffect(() => {
        const dentistIdFromUser = sessionStorage.getItem('dentistIdFromUser');
        if (dentistIdFromUser && dentistIdFromUser !== 'null' && dentistIdFromUser !== 'undefined') {
            setSelectedDentistId(dentistIdFromUser);
        }

        getComboBoxDentists();
    }, [open, getComboBoxDentists]);

    const savePrescription = () => {
        if (!selectedDentistId || medicinesToSave.length === 0) {
            setModalError('É obrigatório informar dentista, data e adicionar pelo menos um medicamento.');
            return;
        }

        const prescription = {
            "dentistaId": selectedDentistId,
            "pacienteId": patientId,
            "data": date,
            "medicamentos": medicinesToSave
        };

        setLoading(true);
        api.post("/receituario", prescription, {
            headers: {
                Authorization: sessionStorage.getItem('token')
            }
        }).then((returnFromApi) => {
            setLoading(false);
            cleanFields();
            onSave(returnFromApi);
        }).catch((returnFromApi) => {
            setLoading(false);
            setModalError(returnFromApi.response.data);
            console.error(returnFromApi);
            onSave(returnFromApi);
        });
    };

    function adicionarMedicamentoNaLista() {
        let medicine = {
            "nome": medicineToAdd,
            "descricaoQuantidade": quantityToAdd,
            "posologia": dosageToAdd
        };

        setMedicinesToSave(medicinesToSave => [...medicinesToSave, medicine]);
    }

    function removerMedicamentoDaLista(medicamento) {
        medicinesToSave.forEach(() => {
            const indexMedicamento = medicinesToSave.indexOf(medicamento);
            medicinesToSave.splice(indexMedicamento, 1);
        });
        setMedicinesToSave(medicinesToSave => [...medicinesToSave]);
    }

    function cleanFields() {
        setMedicineToAdd('');
        setQuantityToAdd('');
        setDosageToAdd('');
        setMedicinesToSave([]);
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">

            <Loading isToHidden={isLoading === false} />

            <DialogTitle>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Receituário
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                <Collapse in={!!modalError} sx={{ mt: 1, mb: 1 }}>
                    <Alert severity="error" onClose={() => setModalError('')}>
                        {modalError}
                    </Alert>
                </Collapse>

                <Grid container spacing={2}>
                    <Grid size={7}>
                        <FormControl fullWidth>
                            <InputLabel id="dentist-select-label">Dentista</InputLabel>
                            <Select fullWidth
                                labelId="dentist-combobox-label"
                                id="dentist-combobox"
                                value={selectedDentistId}
                                label="Dentista"
                                onChange={(e) => setSelectedDentistId(e.target.value)}>

                                {comboBoxDentists.map((dentist) => (
                                    <MenuItem value={dentist.id}>{dentist.nome}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={5}>
                        <DatePicker
                            label="Data"
                            value={date}
                            onChange={(newValue) => setDate(newValue)}
                            slotProps={{ textField: { fullWidth: true } }}
                        />
                    </Grid>
                    <Grid size={9}>
                        <TextField
                            fullWidth
                            label="Medicamento"
                            value={medicineToAdd}
                            onChange={(e) => setMedicineToAdd(e.target.value)}
                        />
                    </Grid>
                    <Grid size={3}>
                        <TextField
                            fullWidth
                            label="Quantidade"
                            value={quantityToAdd}
                            onChange={(e) => setQuantityToAdd(e.target.value)}
                        />
                    </Grid>
                    <Grid size={12}>
                        <TextField
                            fullWidth
                            label="Posologia"
                            multiline
                            rows={2}
                            value={dosageToAdd}
                            onChange={(e) => setDosageToAdd(e.target.value)}
                        />
                    </Grid>
                </Grid>

                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => adicionarMedicamentoNaLista()} variant="outlined">Adicionar</Button>
                </DialogActions>

                <DialogContent dividers>
                    {medicinesToSave.length === 0 && (
                        <Typography align="center" sx={{ py: 4 }} >
                            Nenhum medicamento adicionado ainda.
                        </Typography>
                    )}
                    <List disablePadding>
                        {medicinesToSave.map((medicine) => (
                            <React.Fragment>
                                <ListItem
                                    key={medicine}
                                    disableGutters
                                    alignItems="flex-start"
                                    secondaryAction={
                                        <IconButton aria-label="Deletar" color="error" onClick={() => removerMedicamentoDaLista(medicine)}>
                                            <Trash size={18} />
                                        </IconButton>
                                    }
                                >
                                    <ListItemText
                                        primary={<Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}> {medicine.nome} </Typography>}
                                        secondary={<React.Fragment> <Typography variant="body2" color="text.primary" > {medicine.descricaoQuantidade}</Typography>
                                            <Typography
                                                variant="body2"
                                            >
                                                {medicine.posologia}
                                            </Typography>
                                        </React.Fragment>
                                        }
                                    />
                                </ListItem>
                            </React.Fragment>
                        ))}
                    </List>
                </DialogContent>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose}>Cancelar</Button>
                <Button onClick={savePrescription} variant="contained">Salvar</Button>
            </DialogActions>
        </Dialog>
    );
}